import { z } from 'zod';
/**
 * BrainStreamEvent — the normalized event surface consumed by both
 * the API server (orchestrator → SSE) and the web client (SSE →
 * transcript). Shared here so both sides validate against the
 * same Zod schema with the same strip-on-parse behavior.
 *
 * Per [ADR-0006]:
 * - **Consumers MUST ignore unknown `type` values** (parse returns
 *   null; caller warn-logs and drops the event).
 * - **Publishers MUST NOT remove or rename event types** without a
 *   superseding ADR. Adding new types is non-breaking; removing is
 *   breaking.
 * - **Field additions on existing types are non-breaking** when the
 *   added field is optional both in type and in behavior.
 *
 * Why strip unknown fields?
 * 1. **R2 (prompt-leak) defense.** A hostile producer that injects
 *    a `contextPrompt` field on a known event type would otherwise
 *    leak it to the SSE consumer. Strict-but-strip prevents that.
 * 2. **Forward-compatibility.** Producers on a newer version that
 *    add NEW optional fields don't break older consumers.
 */
const utteranceSchema = z.object({
    type: z.literal('utterance'),
    speaker: z.enum(['tutor', 'student']),
    text: z.string(),
    ts: z.string(),
});
const moderationFlagSchema = z.object({
    type: z.literal('moderation_flag'),
    severity: z.enum(['info', 'warn', 'block']),
    reason: z.string(),
});
const turnCompleteSchema = z.object({
    type: z.literal('turn_complete'),
});
const practiceTargetSchema = z.object({
    type: z.literal('practice_target'),
    event_id: z.string().min(1).max(128),
    repeat_target: z.string().min(1).max(500),
    pattern: z.string().max(300).optional(),
});
const errorSchema = z.object({
    type: z.literal('error'),
    message: z.string(),
});
export const brainStreamEventSchema = z.discriminatedUnion('type', [
    utteranceSchema,
    moderationFlagSchema,
    practiceTargetSchema,
    turnCompleteSchema,
    errorSchema,
]);
/**
 * Parse a raw SSE / Redis message string into a sanitized
 * BrainStreamEvent. Returns null on:
 *   - JSON parse failure (malformed payload)
 *   - Schema validation failure (unknown `type`, missing required fields)
 *
 * On success, unknown fields are stripped (Zod default strip behavior).
 * Per ADR-0006 §Forward-compat: unknown event TYPES MUST be ignored
 * and warn-logged — caller is responsible for the warn log on null.
 */
export function parseBrainStreamEvent(raw) {
    let candidate;
    try {
        candidate = JSON.parse(raw);
    }
    catch {
        return null;
    }
    const result = brainStreamEventSchema.safeParse(candidate);
    if (!result.success)
        return null;
    return result.data;
}
//# sourceMappingURL=brain-events.js.map